<template>
  <div>
    <page-header
      :actions="pageHeaderActionsConfig"
      :page="pageHeaderConfig"
      right-side-class="col-sm-12 col-md-4 align-items-end d-flex pb-2 justify-content-end"
    />

    <section-title
      bg-icon="bg-green"
      class="my-4"
      :has-line="false"
      icon="fas fa-bell"
      :reverse="true"
      title="Certificación de Conflicto de Interés"
    />

    <div class="ml-md-4">
      <h1 class="title">
        CERTIFICACIÓN DE AUSENCIA DE CONFLICTO DE INTERESES PARA EVALUADORES DE
        PROPUESTAS SOMETIDAS A LA SECRETARÍA ASOCIADA DE
        <br />
        EDUCACIÓN ESPECIAL DEL DEPARTAMENTO DE EDUCACIÓN
      </h1>

      <p>
        Su designación como miembro del Comité Evaluador de Propuestas del
        Departamento de Educación requiere de su conocimiento sobre la
        reglamentación federal relacionada al conflicto de intereses y a la
        naturaleza privilegiada de este proceso. Antes de evaluar propuestas
        como Evaluador Principal o Secundario, usted debe informar a la
        Secretaría Auxiliar de Asuntos Federales sobre cualquier conflicto de
        intereses o aparente conflicto con este proceso.
      </p>

      <p>
        <span class="font-weight-bold">Conflicto de Interés:</span>
        Un conflicto de interés o aparente conflicto puede ocurrir si usted
        tiene directa o indirectamente alguna afiliación con la organización que
        sometió una propuesta a esta agencia para evaluación bajo algún programa
        para el cual usted ha sido designado evaluador. Como evaluador, usted
        debe informar al personal de la Secretaría Auxiliar de Asuntos Federales
        sobre cualquier conflicto potencial que puede existir entre usted y la
        organización. A continuación se describen ejemplos de posibles
        situaciones conflictivas:
      </p>
      <p>
        Someter una propuesta personal al Departamento de Educación: Si ha
        sometido una propuesta o ha estado involucrado personalmente con la
        preparación de una propuesta para evaluación bajo algún programa para el
        cual usted ha sido designado como evaluador, no puede ser parte del
        Comité Evaluador de Propuestas.
      </p>
      <p>
        Tener algún tipo de afiliación con la institución u organización que
        sometió propuesta para evaluación bajo algún programa para el cual usted
        ha sido designado como evaluador. Un conflicto puede estar presente si
        usted:
      </p>

      <p class="mb-0">
        - Tiene un empleo actual o está siendo considerado para empleo en la
        organización solicitante.
      </p>

      <p class="mb-0">
        - Realizó algún arreglo formal o informal de empleo con la organización,
        incluyendo asesoramiento, consultoría u otra posición similar.
      </p>
      <p class="mb-0">
        - Posee membresía actual con el comité visitante, junta directiva o un
        cuerpo similar de la organización.
      </p>
      <p class="mb-0">
        - Tiene cualquier oficina, membresía en la junta de gobierno o comité
        relevante dentro de la organización.
      </p>
      <p class="mb-0">
        - Es estudiante activo (esto sería un conflicto si una de las propuestas
        sometidas provienen de un departamento o escuela dentro del cual es un
        colaborador o estudiante).
      </p>
      <p>
        - Recibió o retiene premio de la organización solicitante por los
        pasados doce (12) meses.
      </p>

      <p>
        Tener relación con algún individuo que tenga interés personal en la
        propuesta para evaluación bajo algún programa para el cual usted ha sido
        designado como evaluador como resultado de:
      </p>

      <p class="mb-0">- Matrimonio o relación familiar.</p>
      <p class="mb-0">- Relación profesional o de negocios.</p>
      <p class="mb-0">
        - Empleo en la misma institución por los pasados doce (12) meses.
      </p>
      <p class="mb-0">
        - Su asociación pasada o presente como consejero de tesis o estudiante
        de tesis.
      </p>
      <p>
        - Su colaboración en un proyecto, libro, artículo o informe en los
        pasados cuarenta y ocho (48) meses.
      </p>

      <p class="mb-0">Otras afiliaciones o relaciones:</p>
      <p>
        Los intereses de las siguientes personas deben ser tratados como si
        fuesen suyos: esposo(a), hijos, familiares que residen en su hogar
        inmediato o cualquier persona que se le considere legalmente su
        compañero para el cual usted entiende que puede ser cubierto por las
        secciones 1, 2 o 3 de esta certificación.
      </p>
      <p>
        Cualquier otra persona con la cual usted tenga una relación de amistad o
        que usted crea que pueda afectarle su juicio.
      </p>
      <p>
        <span class="font-weight-bold">Confidencialidad de Evaluadores:</span>
        La identidad de los evaluadores es confidencial. Las organizaciones
        pueden solicitarle a la Secretaría Asociada de Educación Especial,
        información sobre la evaluación de su propuesta. No obstante, cuando
        dicha información es divulgada a la organización solicitante, los
        nombres de los evaluadores deben ser eliminados y protegidos. Como
        evaluador, usted no debe discutir los nombres de los evaluadores con los
        proponentes o con alguna otra persona.
      </p>
    </div>

    <div class="ml-md-2">
      <validation-observer ref="noConflicts">
        <validation-provider v-slot="{ errors }" rules="required">
          <question-box-radio-button
            v-model="answer"
            custom-error-msg="Favor seleccionar una de las opciones."
            :error="errors[0]"
            :label="question"
            :options="answers"
          />
        </validation-provider>
      </validation-observer>
    </div>

    <div v-if="answer && answer.value === 'NO'">
      <section-title
        bg-icon="bg-green"
        class="my-4 ml-md-2"
        :has-line="false"
        icon="fas fa-bell"
        :reverse="true"
        title="Certificación de Confidencialidad de Datos"
      />
      <p class="ml-md-4">
        Como evaluador me comprometo a mantener en estricta confidencialidad la
        información presentada en la propuesta asignada. Dicha información será
        exclusivamente utilizada para propósitos de evaluación. En este proceso
        me comprometo a no reproducir, modificar, hacer pública o divulgar a
        terceros la información sobre la propuesta evaluada.
        <span class="font-weight-bold">
          De no cumplir con este compromiso, seré responsable de las
          consecuencias o repercusiones que dicho incumplimiento llegase a
          ocasionarle a la Secretaría Asociada de Educación Especial y al
          Departamento de Educación de Puerto Rico.
        </span>
      </p>
    </div>

    <div class="d-flex mt-4 justify-content-end">
      <action-button
        class="mr-2"
        i="fas fa-arrow-circle-left"
        variant="outline-success"
        @click="goBack"
      >
        Regresar
      </action-button>
      <action-button
        v-if="canContinue"
        i="fas fa-arrow-circle-right"
        variant="success"
        @click="confirmationHandler"
      >
        Continuar
      </action-button>
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import QuestionBoxRadioButton from '@/views/provider/proposalHistory/components/QuestionBoxRadioButton.vue';
import alertMixin from 'utils/mixins/alert.mixin';
import ActionButton from 'ActionButton';
import { evaluatorConflictCert } from '@/modules/serviceProposalHistory/api';
import { name as moduleName } from '@/modules/serviceProposalHistory/store';

export default {
  name: 'EvaluatorConflictsConfirmation',
  components: {
    PageHeader,
    SectionTitle,
    QuestionBoxRadioButton,
    ActionButton,
  },
  mixins: [alertMixin],
  data: () => ({
    moduleName,
    pageHeaderConfig: {
      icon: 'fas fa-user-tie',
      title: 'Propuesta de Servicio',
      routes: ['Proveedor', 'Propuesta de Servicio'],
    },
    question: `Certifico que he leído y entiendo la Certificación de Conflicto de
        Intereses y que, de acuerdo con mi mejor conocimiento, no tengo interés
        económico o conflicto de intereses con ninguna de las propuestas
        presentadas para evaluación bajo algún programa para el cual he sido
        designado como evaluador. Reconozco que esta certificación continúa en
        efecto hasta que termine el proceso de evaluación. Si descubro que
        existe conflicto de intereses o aparente conflicto con alguna de las
        propuestas sometidas para evaluación bajo algún programa para el cual
        usted he sido designado como evaluador, lo informaré inmediatamente al
        coordinador del proceso.`,
    answer: null,
    answers: [
      {
        label:
          'No tengo conflicto de interés por lo que puedo continuar con la Evaluación de Propuesta',
        value: 'NO',
        check: false,
      },
      {
        label:
          'Tengo conflicto de interés por lo que no deseo continuar con la Evaluación de Propuesta',
        value: 'SI',
        check: false,
      },
    ],
  }),
  computed: {
    canContinue() {
      if (!this.answer || (this.answer && this.answer.value === 'SI')) {
        return false;
      }

      return true;
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.goBack(),
        },
        {
          text: 'Continuar',
          icon: 'fas fa-arrow-circle-right',
          variant: 'success',
          visible: this.canContinue,
          click: () => this.confirmationHandler(),
        },
      ];
    },
  },
  watch: {
    async answer() {
      await this.changeAnswer();
    },
  },
  methods: {
    async confirmationHandler() {
      const isValid = await this.$refs.noConflicts.validate();
      if (!isValid) {
        this.Toast(
          'Alerta',
          'Favor completar la información para poder continuar.',
          'error'
        );
        return;
      }
      this.$router.push({
        name: 'ServiceProposalEvaluation',
        params: {
          proposalRequestId: this.$route.params.proposalRequestId,
        },
      });
    },
    goBack() {
      this.$router.push({
        name: 'ServiceProposalHistory',
      });
    },
    async changeAnswer() {
      if (!this.answer || (this.answer && this.answer.value === 'NO')) {
        return;
      }
      const answer = await this.ShowCancelModal(
        'Confirmación',
        'Según su contestación, usted tiene conflicto de interés con la propuesta a evaluar. ¿Es correcto?',
        'Si',
        'No'
      );

      if (!answer) return;
      await evaluatorConflictCert(this.$route.params.proposalRequestId)
        .then(() => {
          this.unRegisteProposalOfPersistency();

          this.$router.push({
            name: 'ServiceProposalHistory',
            params: {
              reload: true,
            },
          });
        })
        .catch((err) => {
          if (err.response != null)
            this.Toast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
        });
    },
    unRegisteProposalOfPersistency() {
      const moduleName = this.moduleName || 'moduleGenerated';
      if (this.$store.state[moduleName] != undefined) {
        this.$store.unregisterModule(moduleName);
      }
      const moduleData = JSON.parse(sessionStorage.getItem(moduleName));
      moduleData.ServiceProposalHistory.results = moduleData.ServiceProposalHistory.results.filter(
        (x) => x.proposalServiceId != this.$route.params.proposalRequestId
      );
      sessionStorage.setItem(moduleName, JSON.stringify(moduleData));
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

.title {
  font-size: 1rem;
  font-weight: bold;
  text-align: center;
  color: map-get($colors, 'black');
}

p {
  color: map-get($colors, 'black');
}
</style>
